import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  useForm
} from 'react-hook-form';
import { BaseResponse } from '@utils/types/shared/base.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { UserResponse } from '@utils/types/profile/user-profile.ts';
import { Save } from 'lucide-react';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import { Card } from '@components/atoms/ui-kit/card';
import { Text } from '@components/atoms/ui-kit/text';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ChangePasswordPayload,
  useChangePassword
} from '@utils/api/users/use-change-password.tsx';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { useState } from 'react';

type AccountDetailsFormProps = {
  control: Control<UserResponse>;
  errors: FieldErrors<UserResponse>;
  onSubmit: (data: UserResponse) => void;
  user: BaseResponse<UserResponse> | undefined;
  handleAccountSubmit: UseFormHandleSubmit<UserResponse>;
  isAccountDirty: boolean;
};

export default function AccountDetailsForm({
  control,
  errors,
  onSubmit,
  user,
  handleAccountSubmit,
  isAccountDirty
}: AccountDetailsFormProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const changePasswordMutation = useChangePassword();

  const passwordSchema = yup.object().shape({
    old_password: yup.string().required(t('old-password-is-required')),
    new_password: yup
      .string()
      .required(t('new-password-is-required'))
      .min(12, t('password-must-be-at-least-12-characters'))
      .matches(/[A-Z]/, t('password-must-have-at-least-one-uppercase-letter'))
      .matches(/[a-z]/, t('password-must-have-at-least-one-lowercase-letter'))
      .matches(/\d/, t('password-must-have-at-least-one-number')),
    confirm_new_password: yup
      .string()
      .required(t('confirm-password-is-required'))
      .oneOf([yup.ref('new_password')], t('passwords-must-match'))
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors, isDirty: isPasswordDirty }
  } = useForm({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      old_password: '',
      new_password: '',
      confirm_new_password: ''
    }
  });

  const onSubmitPassword = (data: ChangePasswordPayload) => {
    setIsSubmitting(true);
    changePasswordMutation.mutate(
      {
        old_password: data.old_password,
        new_password: data.new_password
      },
      {
        onSuccess: () => {
          toast.success(t('password-changed-successfully'));
          resetPasswordForm();
          setIsSubmitting(false);
        },
        onError: (error: AxiosError<BaseError>) => {
          const fallbackMessage = t(
            'failed-to-change-password-please-try-again'
          );
          const data = error?.response?.data;

          let messages: string[] = [];

          if (Array.isArray(data?.detail)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            messages = data.detail.map((err: any) => {
              const field = err.loc?.[1] ?? 'field';
              return `${field}: ${err.msg}`;
            });
          } else if (data?.message) {
            messages = [data.message];
          } else if (data?.error_description) {
            messages = [data.error_description];
          } else {
            messages = [fallbackMessage];
          }

          toast.error(messages.join('\n'));
          setIsSubmitting(false);
        }
      }
    );
  };

  return (
    <Card padding="sm" className="sm:p-6">
      <form onSubmit={handleAccountSubmit(onSubmit)}>
        <PageHeader
          title={t('account-details')}
          caption={t('update-account-description')}
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        >
          <Button
            icon={<Save width={16} />}
            type="submit"
            label={t('update')}
            variant="secondary"
            size="small"
            disabled={!isAccountDirty}
          />
        </PageHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-6">
          <Controller
            name="first_name"
            control={control}
            defaultValue={user?.result.first_name || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('first-name')}
                id="first_name"
                name="first_name"
                autoComplete="given-name"
                error={errors.first_name && errors.first_name?.message}
              />
            )}
          />

          <Controller
            name="last_name"
            control={control}
            defaultValue={user?.result.last_name || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('last-name')}
                id="last_name"
                name="last_name"
                autoComplete="family-name"
                error={errors.last_name && errors.last_name?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            defaultValue={user?.result.email || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('email')}
                id="email"
                name="email"
                autoComplete="email"
                error={errors.email && errors.email?.message}
              />
            )}
          />

          <div className="flex items-end pb-1">
            <Controller
              name="newsletter"
              control={control}
              defaultValue={user?.result.newsletter ?? false}
              render={({ field }) => (
                <Checkbox
                  id="newsletter"
                  name="newsletter"
                  label={t('get-newsletter-updates')}
                  checked={!!field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  variant="primary"
                  error={errors.newsletter?.message}
                />
              )}
            />
          </div>
        </div>
      </form>

      {/* Password Section */}
      <div className="mt-6 pt-6 border-t border-hairline">
        <form onSubmit={handlePasswordSubmit(onSubmitPassword)}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div>
              <Text as="h2" size="sm" weight="semibold">
                {t('change-password')}
              </Text>
              <Text as="p" size="xs" tone="muted" className="mt-0.5">
                {t('change-your-password-here')}
              </Text>
            </div>
            <Button
              icon={<Save width={16} />}
              type="submit"
              label={isSubmitting ? t('updating') : t('update-password')}
              variant="secondary"
              size="small"
              disabled={!isPasswordDirty || isSubmitting}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-6">
            <Controller
              name="old_password"
              control={passwordControl}
              render={({ field }) => (
                <Input
                  {...field}
                  id="old_password"
                  name="old_password"
                  type="password"
                  label={t('current-password')}
                  autoComplete="current-password"
                  placeholder={t('current-password')}
                  error={passwordErrors.old_password?.message?.toString()}
                />
              )}
            />

            <Controller
              name="new_password"
              control={passwordControl}
              render={({ field }) => (
                <Input
                  {...field}
                  id="new_password"
                  label={t('new-password')}
                  name="new_password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('new-password')}
                  error={passwordErrors.new_password?.message?.toString()}
                />
              )}
            />
            <Controller
              name="confirm_new_password"
              control={passwordControl}
              render={({ field }) => (
                <Input
                  {...field}
                  id="confirm_new_password"
                  name="confirm_new_password"
                  label={t('confirm-new-password')}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('confirm-new-password')}
                  error={passwordErrors.confirm_new_password?.message?.toString()}
                />
              )}
            />
          </div>
        </form>
      </div>
    </Card>
  );
}
